import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Paper, CircularProgress, Alert, Stack
} from '@mui/material';
import * as api from './intrastatsApi';
import type { Intrastat } from './intrastatsApi';
import { sx14, paperSx, FormRow, DetailHeader } from '../common/detailView';

interface Props {
    intrastatId: number | null;
}

type StringField =
    | 'code'
    | 'internalDescription'
    | 'externalCode1'
    | 'externalDescription1'
    | 'externalCode2'
    | 'externalDescription2'
    | 'externalCode3'
    | 'externalDescription3';

const FIELDS: Array<{ key: StringField; labelKey: string; maxLength: number }> = [
    { key: 'code', labelKey: 'intrastat.code', maxLength: 12 },
    { key: 'internalDescription', labelKey: 'intrastat.internalDescription', maxLength: 100 },
    { key: 'externalCode1', labelKey: 'intrastat.externalCode1', maxLength: 12 },
    { key: 'externalDescription1', labelKey: 'intrastat.externalDescription1', maxLength: 100 },
    { key: 'externalCode2', labelKey: 'intrastat.externalCode2', maxLength: 12 },
    { key: 'externalDescription2', labelKey: 'intrastat.externalDescription2', maxLength: 100 },
    { key: 'externalCode3', labelKey: 'intrastat.externalCode3', maxLength: 12 },
    { key: 'externalDescription3', labelKey: 'intrastat.externalDescription3', maxLength: 100 },
];

export function IntrastatView({ intrastatId }: Props) {
    const { t } = useTranslation();

    const [item, setItem] = useState<Intrastat | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [copy, setCopy] = useState<Intrastat | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (intrastatId === null) {
                setItem({ id: 0, code: '' });
                setIsNew(true);
                setIsEditing(true);
            } else {
                const data = await api.getIntrastat(intrastatId);
                setItem(data);
                setIsNew(false);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [intrastatId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error && !item) return <Alert severity="error">{error}</Alert>;
    if (!item) return null;

    const startEdit = () => { setCopy(JSON.parse(JSON.stringify(item))); setIsEditing(true); };
    const cancelEdit = () => { if (copy) setItem(copy); setIsEditing(false); };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            const response = await api.saveIntrastat(item);
            setItem(response);
            setIsNew(false);
            setIsEditing(false);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const del = async () => {
        if (!window.confirm(t('intrastat.confirmDelete'))) return;
        setSaving(true); setError(null);
        try {
            await api.deleteIntrastat(item.id);
            window.history.back();
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const update = (field: StringField, value: string) => {
        setItem({ ...item, [field]: value });
    };

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={`${item.code ?? ''} ${item.internalDescription ?? ''}`}
                isEditing={isEditing}
                isNew={isNew}
                saving={saving}
                canSave
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
                onDelete={del}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    {FIELDS.map(f => {
                        // The legacy view hides the code in display mode for existing
                        // rows unless the row is in edit mode. Keep that behavior for
                        // the code field only.
                        const hideDisplay = f.key === 'code' && isNew;
                        return (
                            <FormRow key={f.key} label={t(f.labelKey)}>
                                {!isEditing && !hideDisplay ? (
                                    <Typography sx={sx14}>{item[f.key]}</Typography>
                                ) : (
                                    <TextField size="small" fullWidth
                                        inputProps={{ maxLength: f.maxLength }}
                                        value={item[f.key] ?? ''}
                                        onChange={e => update(f.key, e.target.value)} />
                                )}
                            </FormRow>
                        );
                    })}
                </Stack>
            </Box>

            {saving && <Box display="flex" justifyContent="center" p={1}><CircularProgress size={20} /></Box>}
        </Paper>
    );
}
